/**
 * Dev-only plugin that exposes welcome banner utilities on `window.__traverseWelcome`.
 *
 * Usage in the browser console (development only):
 *
 *   // Show the welcome banner for a workspace
 *   __traverseWelcome.preview('<workspaceId>')
 *
 *   // Clear the banner state for one workspace
 *   __traverseWelcome.clearState('<workspaceId>')
 *
 *   // Clear ALL welcome banner entries from localStorage
 *   __traverseWelcome.clearAll()
 *
 * After calling preview, reload the page to see the banner.
 * The current user's ID is resolved automatically from the user store.
 */
import { useUserStore } from '~/stores/user'

const KEY_PREFIX = 'traverse.welcomeBanner.'

declare global {
  interface Window {
    __traverseWelcome?: {
      preview: (workspaceId: string) => void
      clearState: (workspaceId: string) => void
      clearAll: () => void
    }
  }
}

export default defineNuxtPlugin(() => {
  if (!import.meta.dev) return

  function getUserId(): string {
    const id = useUserStore().user?.id
    if (!id) {
      console.warn(
        '[traverseWelcome] No user ID available — is the user signed in?',
      )
    }
    return id ?? ''
  }

  window.__traverseWelcome = {
    preview: (workspaceId) => {
      const userId = getUserId()
      if (userId)
        localStorage.setItem(`${KEY_PREFIX}${userId}.${workspaceId}`, 'true')
    },
    clearState: (workspaceId) => {
      const userId = getUserId()
      if (userId)
        localStorage.removeItem(`${KEY_PREFIX}${userId}.${workspaceId}`)
    },
    clearAll: () => {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(KEY_PREFIX))
        .forEach((k) => localStorage.removeItem(k))
    },
  }
})
